import React, { forwardRef, TextareaHTMLAttributes } from 'react'
import { clsx } from 'clsx'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  hint?: string
  isLoading?: boolean
  resize?: boolean
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ 
    label, 
    error, 
    hint, 
    isLoading,
    resize = true,
    className,
    disabled,
    rows = 3,
    ...props 
  }, ref) => {
    const textareaId = props.id || `textarea-${crypto.randomUUID()}`
    
    return (
      <div className="w-full">
        {label && (
          <label 
            htmlFor={textareaId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
            {props.required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          <textarea
            ref={ref}
            id={textareaId}
            rows={rows}
            disabled={disabled || isLoading}
            className={clsx(
              // Base styles
              'block w-full rounded-md border shadow-sm',
              'focus:outline-none focus:ring-2 focus:ring-offset-0',
              'placeholder-gray-400 text-gray-900',
              'transition-colors duration-200',
              'px-3 py-2',
              
              // Resize
              resize ? 'resize-y' : 'resize-none',
              
              // States
              error 
                ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-500'
                : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
              
              (disabled || isLoading) && 'bg-gray-50 text-gray-500 cursor-not-allowed',
              
              className
            )}
            {...props}
          />
          
          {isLoading && (
            <div className="absolute top-2 right-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
            </div>
          )}
        </div>
        
        {(error || hint) && (
          <div className="mt-1">
            {error && (
              <p className="text-sm text-danger-600">{error}</p>
            )}
            {hint && !error && (
              <p className="text-sm text-gray-500">{hint}</p>
            )}
          </div>
        )}
      </div>
    )
  }
)

Textarea.displayName = 'Textarea'

export default Textarea